import ReactTable from 'react-table';
import * as React from 'react';
import * as moment from 'moment';
import style from './style.module.scss';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import { ErrorLogEntry } from 'types/error_log_entry';
import { Answer } from '../types';
import AnswerResultIcon from './answer_result_icon';
import AnswerDifficulty from './answer_difficulty';
import Tag from '@magoosh/lib/tag';
import cx from 'classnames';
import { paths } from 'config/path_helpers';
import Trackable from '@magoosh/utils/trackable';
import trackEvent from 'utilities/track_event';
import AnswerFlag from '@magoosh/organisms/answer_flag';

export default function AnswerReviewTable({ answers, isLoading, showingStudentAnswers, forHsDiagnostic }) {
  const columns: any[] = [
    {
      Header: null, // This column is for the error logging expand/collapse button
      headerStyle: { borderRight: 'none' },
      expander: true,
      style: { justifyContent: 'center' },
      Expander: ({ original, isExpanded }) =>
        canExpandRow(original) && (
          <button className={style.expandArrow}>
            {isExpanded ? <FontAwesomeIcon icon="chevron-down" /> : <FontAwesomeIcon icon="chevron-right" />}
          </button>
        ),
      resizable: false,
      width: 30
    },
    {
      Header: null, // This column is for the answer icon
      headerStyle: { borderRight: 'none' },
      accessor: 'isCorrect',
      Cell: props => <AnswerResultIcon {...props.original} />,
      resizable: false,
      width: 30
    },
    {
      Header: 'Question Title',
      accessor: 'question.title',
      width: 325,
      Cell: props => (
        <span>
          <Trackable name="review_page_answer" properties={{ hadLogEntry: !!props.original.errorLogEntry }}>
            <a href={props.original.url}>{props.value}</a>
          </Trackable>
        </span>
      )
    },
    {
      Header: 'Section',
      accessor: 'examSection.displayName',
      width: 200
    },
    {
      Header: 'Subject',
      accessor: 'subjectTags',
      width: showingStudentAnswers ? 225 : 120
    },
    {
      Header: 'Difficulty',
      accessor: 'sortableDifficulty',
      width: 85,
      Cell: props => <AnswerDifficulty {...props.original} />
    },
    {
      Header: showingStudentAnswers ? 'Pace' : 'Your Pace',
      width: 55,
      accessor: 'yourPace',
      Cell: props => (
        <span className={props.value <= props.original.othersPace ? 'text-success' : 'text-danger'}>
          {props.original.showPaceData ? formatPace(props.value) : ''}
        </span>
      )
    },
    {
      Header: "Others' Pace",
      width: 70,
      accessor: 'othersPace',
      Cell: props => (props.original.showPaceData ? formatPace(props.value) : '')
    },
    {
      Header: 'Date',
      accessor: 'createdAt',
      width: 80,
      Cell: props => <span>{moment(props.value * 1000).format('MMM DD')}</span>
    }
  ];

  if (!showingStudentAnswers && !forHsDiagnostic) {
    columns.push({
      Header: 'Log Entry',
      Expander: ({ isExpanded, original }) => {
        return (
          !isExpanded &&
          canExpandRow(original) && (
            <button className="small" style={{ background: 'none', border: 'none' }}>
              <a>View</a>
            </button>
          )
        );
      },
      expander: true,
      resizable: true,
      width: 60
    },
    {
      Header: 'Flag',
      accessor: 'isFlagged',
      style: { justifyContent: 'center' },
      width: 45,
      sortable: false,
      Cell: props => <AnswerFlag flagged={props.value} promptId={props.original.prompt.id} />
    });
  }

  return (
    <div className={style.myTable}>
      <ReactTable
        data={answers}
        columns={columns}
        showPagination={false}
        defaultPageSize={10000}
        multiSort={false}
        loading={isLoading}
        minRows={0}
        onExpandedChange={(newExpanded, index, event, oldRowState) => {
          if (!oldRowState.isExpanded) {
            // User just expanded a row
            trackEvent('review_page_row');
          }
        }}
        SubComponent={({ original }) => {
          const errorLogEntry: ErrorLogEntry = original.errorLogEntry;

          if (!errorLogEntry) {
            return null;
          }

          return (
            <div className={style.errorLogEntryWrapper}>
              <div className={style.errorLogEntry}>
                <div>
                  <strong>Entry Added: {moment(errorLogEntry.createdAt).format('MMMM DD')}</strong>
                  <br />
                  {errorLogEntry.errorLogTags.map(tag => (
                    <Tag
                      key={tag.id}
                      canBeSelected={false}
                      displayName={tag.displayName}
                      isSelected={true}
                      onClickCallback={null}
                    />
                  ))}
                </div>
                <div>
                  <strong>What happened / Next time I should:</strong>
                  <p className={cx(style.entryTextContainer, 'small')}>
                    {errorLogEntry.guidedReflectionText}
                  </p>

                  <strong>General Note:</strong>
                  <p className={cx(style.entryTextContainer, 'small')}>
                    {errorLogEntry.unguidedReflectionText}
                  </p>
                </div>
              </div>
            </div>
          );
        }}
        NoDataComponent={() => (
          <div className={style.noAnswersFound}>
            No answers found.
            <br />
            <a className="btn btn-primary u-margin-V-s" href={paths.startPractice}>
              Start practicing!
            </a>
          </div>
        )}
      />
    </div>
  );
}

function formatPace(seconds: number) {
  const duration = moment.duration(seconds, 'seconds');
  return `${duration.minutes()}:${duration
    .seconds()
    .toString()
    .padStart(2, '0')}`;
}

function canExpandRow(row: Answer) {
  return !!row.errorLogEntry;
}
